import { useMemo, useState } from "react";
import { Grid, Box, Typography, Tooltip, IconButton } from "@mui/material";
import TableComponentV2 from "../../../../common/components/TableComponentV2";
import { useZeroSalesData } from "../hooks";
import LoadingComponent from "../../../../common/components/LoadingComponent";
import moment from "moment";
import StorefrontTwoToneIcon from '@mui/icons-material/StorefrontTwoTone';
import Inventory2TwoToneIcon from '@mui/icons-material/Inventory2TwoTone';
import ContentPasteSearchTwoToneIcon from '@mui/icons-material/ContentPasteSearchTwoTone';
import CheckIcon from '@mui/icons-material/Check';
import CallOutsFilter from "../components/CallOutsFilter";
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';

export default function StoreArticleZeroSalesTable({ filters, overviewTableIsEmpty, toggleState, onDropdownFilterChange }) {
    const { zeroSalesData, isLoadingZeroSalesData } = useZeroSalesData({
        params: { ...filters },
    });

    const hasLastReceivedDate = useMemo(() => {
        if (!zeroSalesData || zeroSalesData?.length === 0) return false;

        return zeroSalesData[0].hasOwnProperty("last_received_date");
    }, [zeroSalesData])

    const [checkedActions, setCheckedActions] = useState({
        storeDisplay: true,
        orderSettings: true,
        stockProblem: true,
        rentableValue: true,
        noCallOuts: true
    });

    const onPillSelectChange = (selectedValues, field) => {
        // selectedValues is the array of selected call-out types like ['storeDisplay', 'stockProblem', ...]
        if (field === 'callOuts' && Array.isArray(selectedValues)) {
            const newCheckedActions = {
                storeDisplay: selectedValues.includes('storeDisplay'),
                orderSettings: selectedValues.includes('orderSettings'),
                stockProblem: selectedValues.includes('stockProblem'),
                rentableValue: selectedValues.includes('rentableValue'),
                noCallOuts: selectedValues.length === 0, // noCallOuts is true when no other actions are selected
            };
            setCheckedActions(newCheckedActions);
        }
    };

    const filteredZeroSalesData = useMemo(() => {
        if (!zeroSalesData) return [];
        if (
            !checkedActions.storeDisplay &&
            !checkedActions.orderSettings &&
            !checkedActions.stockProblem &&
            !checkedActions.rentableValue &&
            !checkedActions.noCallOuts
        ) {
            return zeroSalesData;
        }
        return zeroSalesData.filter((row) => {
            const {
                inventory_value: inventoryValue,
                rolling_week_sales_at_cost: rollingSalesAtCost,
                csl: cslValue,
                rentables_articles: rentable,
                display_type_value: displayTypeValue,
                sales_at_cost_transaction_count: salesAtCostCount,
            } = row;

            let resultDaysSupply = null;
            if (rollingSalesAtCost && rollingSalesAtCost !== 0 && rollingSalesAtCost !== '0') {
                const adjustedInventory = inventoryValue - (inventoryValue * (displayTypeValue / 100));
                const finalizedRollingSalesAtCost = (rollingSalesAtCost / salesAtCostCount);
                resultDaysSupply = adjustedInventory / finalizedRollingSalesAtCost;
            }

            const isRentable = rentable === true;
            const isStockProblem = !isRentable && cslValue <= 75 || resultDaysSupply <= 15;
            const isOrderSettings = !isRentable && cslValue >= 75 && resultDaysSupply > 15 && resultDaysSupply < 45;
            const isStoreDisplay = !isRentable && (cslValue >= 75 && resultDaysSupply >= 45) && !isOrderSettings;

            if (checkedActions.rentableValue && isRentable) return true;
            if (checkedActions.orderSettings && isOrderSettings) return true;
            if (
                checkedActions.storeDisplay &&
                isStoreDisplay &&
                (!checkedActions.orderSettings ? !isOrderSettings : true)
            ) return true;
            if (checkedActions.stockProblem && isStockProblem) return true;

            // Only include for No Call-outs if it doesn't match any other action
            const matchesOtherActions =
                isRentable ||
                isOrderSettings ||
                isStoreDisplay ||
                isStockProblem;

            if (checkedActions.noCallOuts && !matchesOtherActions) return true;

            return false;
        });
    }, [zeroSalesData, checkedActions]);

    const actionCounts = useMemo(() => {
        if (!zeroSalesData) return { storeDisplay: 0, orderSettings: 0, stockProblem: 0, rentableValue: 0, noCallOuts: 0 };
        let storeDisplay = 0, orderSettings = 0, stockProblem = 0, rentableValue = 0, noCallOuts = 0;
        zeroSalesData.forEach(row => {
            const {
                inventory_value: inventoryValue,
                rolling_week_sales_at_cost: rollingSalesAtCost,
                csl: cslValue,
                rentables_articles: rentable,
                display_type_value: displayTypeValue,
                sales_at_cost_transaction_count: salesAtCostCount,
            } = row;

            let resultDaysSupply = null;
            if (rollingSalesAtCost && rollingSalesAtCost !== 0 && rollingSalesAtCost !== '0') {
                const adjustedInventory = inventoryValue - displayTypeValue;
                const finalizedRollingSalesAtCost = (rollingSalesAtCost / salesAtCostCount);
                resultDaysSupply = adjustedInventory / finalizedRollingSalesAtCost;
            }

            const isRentable = rentable === true;
            const isStockProblem = !isRentable && cslValue <= 75 || resultDaysSupply <= 15;
            const isOrderSettings = !isRentable && cslValue >= 75 && resultDaysSupply > 15 && resultDaysSupply && resultDaysSupply < 45;
            const isStoreDisplay = !isRentable && (cslValue >= 75 && resultDaysSupply >= 45) && !isOrderSettings;

            if (isStoreDisplay) storeDisplay++;
            if (isOrderSettings) orderSettings++;
            if (isStockProblem) stockProblem++;
            if (isRentable) rentableValue++;
            if (!isRentable && !isOrderSettings && !isStoreDisplay && !isStockProblem) noCallOuts++;
        });

        return { storeDisplay, orderSettings, stockProblem, rentableValue, noCallOuts };
    }, [zeroSalesData]);

    const calcDaysSupply = (row) => {
        const inventoryValue = row.inventory_value ?? row?.row?.original?.inventory_value;
        const salesAtCostCount = row.sales_at_cost_transaction_count
        const rollingSalesAtCost = row.rolling_week_sales_at_cost
        const displayTypeValue = row.display_type_value

        if (rollingSalesAtCost && rollingSalesAtCost !== 0 && rollingSalesAtCost !== '0') {
            const adjustedInventory = inventoryValue - displayTypeValue / 100;
            const finalizedRollingSalesAtCost = (rollingSalesAtCost / salesAtCostCount);
            return adjustedInventory / finalizedRollingSalesAtCost;
        }
        return null;
    };


    const defaultCallOutsArrangement = ["storeDisplay", "stockProblem", "orderSettings", "rentableValue", "noCallOuts"];

    const calloutRanksAsc = Object.fromEntries(
        defaultCallOutsArrangement.map((k, i) => [k, i])
    );

    // New: centralize your condition here
    const getCalloutType = (row) => {
        const daysSupply = calcDaysSupply(row);
        const cslValue = row?.csl ?? 0;
        const rentable = row?.rentables_articles === true;

        // Parentheses make the intent clear and match your original thresholds.
        const isStockProblem = (!rentable && cslValue <= 75) || (daysSupply !== null && daysSupply <= 15);
        const isOrderSettings = !rentable && cslValue >= 75 && daysSupply !== null && daysSupply > 15 && daysSupply < 45;
        const isStoreDisplay = !rentable && cslValue >= 75 && daysSupply !== null && daysSupply >= 45 && !isOrderSettings;

        if (rentable) return "rentableValue";
        if (isStoreDisplay) return "storeDisplay";
        if (isOrderSettings) return "orderSettings";
        if (isStockProblem) return "stockProblem";
        return "noCallOuts";
    };

    const mustHaveColumns = useMemo(
        () => {
            const columns = [
                {
                    id: "article_description",
                    header: "SKU Description",
                    accessorKey: "article_description",
                    cell: (row) => (
                        <Box sx={{ marginX: 'auto' }}>
                            <Tooltip title={`SKU: ${row?.row?.original?.article_number ?? row?.getValue()}`} placement="left">
                                <Typography marginLeft={'1.5rem'}>{row?.getValue()}</Typography>
                            </Tooltip>
                        </Box>
                    ),
                    customStyle: { width: "35rem", verticalAlign: "middle", paddingY: 1 },
                },
                {
                    id: "product_category",
                    header: "Category",
                    accessorKey: "article_category",
                    cell: (row) => (
                        <Box sx={{ width: { sm: '60%' }, marginX: 'auto' }}>
                            <Typography>{row?.getValue()}</Typography>
                        </Box>
                    ),
                    customStyle: {
                        width: "8rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                },
                {
                    id: "must_have",
                    header: "Must Have Type",
                    accessorKey: "article_sub_category",
                    cell: (row) => {
                        const value = row?.getValue();
                        const isSpecialValue = value === "888" || value === "4k";
                        return (
                            <Box sx={{ width: { sm: '45%' }, marginX: 'auto', display: "flex", gap: .5 }}>
                                {isSpecialValue && <Typography>Top </Typography>}
                                <Typography>{value}</Typography>
                            </Box>
                        );
                    },
                    customStyle: {
                        width: "11rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                },
            ];

            const lastReceivedColumn = {
                id: "last_received_date",
                header: "Last Received Date",
                accessorKey: "last_received_date",
                cell: (row) => {
                    const val = row?.getValue();
                    const displayDateText = val ? moment(val, 'YYYY-MM-DD').format("MMM DD, YYYY") : "-";
                    const humanMoment = val ? moment(val, 'YYYY-MM-DD').fromNow() : "Never";
                    return <Box sx={{
                        width: '7em', marginX: 'auto', display: "flex", gap: .5,
                        justifyContent: !!val ? "flex-start" : "center"
                    }} title={humanMoment}>
                        <Typography>
                            {displayDateText}
                        </Typography>
                    </Box>
                },
                customStyle: {
                    width: "15rem",
                    textAlign: "left",
                    verticalAlign: "middle",
                },
            };

            if (hasLastReceivedDate) {
                columns.splice(4, 0, lastReceivedColumn);
            }

            columns.push(
                {
                    id: "last_sold_date",
                    header: (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            Last Sold Date
                            <Tooltip
                                arrow
                                title={
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            N/A – SME
                                        </Typography>
                                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                                            <li>
                                                <Typography variant="body2">
                                                    Items with inconsistent delivery.
                                                </Typography>
                                            </li>
                                            <li>
                                                <Typography variant="body2">
                                                    Refer to Merch team for specific reason.
                                                </Typography>
                                            </li>
                                        </ul>
                                    </Box>
                                }
                            >
                                <IconButton size="small">
                                    <InfoOutlineIcon fontSize="small" sx={{ color: "#0A4FB4" }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ),
                    accessorKey: "last_sold_date",
                    cell: (row) => {
                        const val = row?.getValue();
                        const articleSubCategory = row?.row?.original?.article_sub_category;

                        const isEmptyDate = val === '-' || val === null || val === undefined;

                        if (articleSubCategory === '4k' && isEmptyDate) {
                            return (
                                <Box sx={{ width: '7em', marginX: 'auto', textAlign: "center" }}>
                                    <Typography>
                                        N/A - SME
                                    </Typography>
                                </Box>
                            );
                        }

                        const displayDateText = val ? moment(val, 'YYYY-MM-DD').format("MMM DD, YYYY") : "-";
                        const humanMoment = val ? moment(val, 'YYYY-MM-DD').fromNow() : "Never";

                        return (
                            <Box
                                sx={{
                                    width: '7em',
                                    marginX: 'auto',
                                    display: "flex",
                                    gap: .5,
                                    justifyContent: val ? "flex-start" : "center"
                                }}
                                title={humanMoment}
                            >
                                <Typography>
                                    {displayDateText}
                                </Typography>
                            </Box>
                        );
                    },
                    customStyle: {
                        width: "15rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                },
                {
                    id: "rentables_articles",
                    header: "is Rentable?",
                    accessorKey: "rentables_articles",
                    cell: (row) => {
                        const value = row?.getValue();
                        return (
                            <Box sx={{ marginX: 'auto' }}>
                                <Typography>{value ? <CheckIcon /> : " "}</Typography>
                            </Box>
                        );
                    },
                    customStyle: {
                        width: "8rem",
                        textAlign: "center",
                        verticalAlign: "middle",
                    },
                },
                {
                    id: "days_supply",
                    header: "Days Supply",
                    accessorFn: calcDaysSupply,
                    cell: (row) => {
                        const value = row.getValue();
                        let displayValue = "-";
                        if (value !== null && value !== undefined) {
                            const num = Number(value);
                            if (Math.abs(num) < 1 && num !== 0) {
                                // For values between -1 and 1 (excluding 0), round away from zero
                                displayValue = Math.sign(num) * 1;
                            } else {
                                // For all other numbers, use normal rounding
                                displayValue = Math.round(num);
                            }
                        }
                        return (
                            <Box sx={{ width: { sm: '30%' }, marginX: 'auto' }}>
                                <Typography textAlign={'center'}>
                                    {displayValue}
                                </Typography>
                            </Box>
                        );
                    },
                    customStyle: {
                        width: "10rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                },
                {
                    id: "csl",
                    header: "CSL",
                    accessorKey: "csl",
                    cell: (row) => {
                        const value = row?.getValue();
                        let displayValue = "0";
                        if (value !== undefined && value !== null) {
                            const num = Number(value);
                            displayValue = Number.isInteger(num) ? num : num.toFixed(0);
                        }
                        return (
                            <Box sx={{ marginX: 'auto' }}>
                                <Typography>
                                    {displayValue}%
                                </Typography>
                            </Box>
                        );
                    },
                    customStyle: {
                        width: "6rem",
                        textAlign: "center",
                        verticalAlign: "middle",
                    },
                },
                {
                    id: "callout",
                    header: "Call-Out",

                    // 1) Provide a sortable value (the type) to the table
                    accessorFn: (row) => getCalloutType(row),

                    // 2) Allow sorting and define the desired ordering
                    enableSorting: true,

                    // Ascending uses defaultCallOutsArrangement order;
                    // Descending automatically reverses it via the table’s sort direction handling.
                    sortingFn: (rowA, rowB, columnId) => {
                        const a = rowA.getValue(columnId);
                        const b = rowB.getValue(columnId);

                        const rank = (v) =>
                            v == null
                                ? Number.POSITIVE_INFINITY
                                : (calloutRanksAsc[v] ?? Number.POSITIVE_INFINITY);

                        const diff = rank(a) - rank(b);
                        if (diff !== 0) return diff;

                        // Optional: tie-break on description for stable sorting
                        const descA = rowA.original?.article_description ?? "";
                        const descB = rowB.original?.article_description ?? "";
                        return descA.localeCompare(descB);
                    },

                    // 3) Render the icon based on the computed call-out type
                    cell: (cellCtx) => {
                        const type = cellCtx.getValue(); // 'storeDisplay' | 'orderSettings' | 'stockProblem' | 'rentableValue' | 'noCallOuts'
                        let icon = null;

                        if (type === "stockProblem") {
                            icon = (
                                <Tooltip title="Stock Problem" placement="left">
                                    <Inventory2TwoToneIcon fontSize="small" sx={{ color: "#F2AC42" }} />
                                </Tooltip>
                            );
                        } else if (type === "orderSettings") {
                            icon = (
                                <Tooltip title="Order Setting" placement="left">
                                    <ContentPasteSearchTwoToneIcon fontSize="small" sx={{ color: "#0a4fb4" }} />
                                </Tooltip>
                            );
                        } else if (type === "storeDisplay") {
                            icon = (
                                <Tooltip title="Store Display" placement="left">
                                    <StorefrontTwoToneIcon fontSize="small" sx={{ color: "#EA324C" }} />
                                </Tooltip>
                            );
                        }
                        // For 'rentableValue' and 'noCallOuts', keep icon null to match your current UI

                        return (
                            <Box sx={{ width: { sm: '30%' }, marginX: 'auto' }}>
                                {icon}
                            </Box>
                        );
                    },
                    customStyle: {
                        width: "8rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                }
            );

            return columns;
        },
        [filteredZeroSalesData, hasLastReceivedDate, filters?.store_id]
    );

    const innovationProductColumns = useMemo(
        () => {
            const columns = [
                {
                    id: "article_description",
                    header: "SKU Description",
                    accessorKey: "article_description",
                    cell: (row) => (
                        <Box sx={{ marginX: 'auto' }}>
                            <Tooltip title={`SKU: ${row?.row?.original?.article_number ?? row?.getValue()}`} placement="left">
                                <Typography marginLeft={'1.5rem'}>{row?.getValue()}</Typography>
                            </Tooltip>
                        </Box>
                    ),
                    customStyle: { width: "20rem", verticalAlign: "middle", paddingY: 1 },
                },
                {
                    id: "sub_category",
                    header: "Sub-Category",
                    accessorKey: "article_sub_category",
                    cell: (row) => (
                        <Box sx={{ width: { sm: '35%' }, marginX: 'auto' }}>
                            <Typography>{row?.getValue()}</Typography>
                        </Box>
                    ),
                    customStyle: {
                        width: "10rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                },
            ];

            const lastReceivedColumn = {
                id: "last_received_date",
                header: "Last Received Date",
                accessorKey: "last_received_date",
                cell: (row) => {
                    const val = row?.getValue();
                    const displayDateText = val ? moment(val, 'YYYY-MM-DD').format("MMM DD, YYYY") : "-";
                    const humanMoment = val ? moment(val, 'YYYY-MM-DD').fromNow() : "Never";
                    return <Box sx={{
                        width: '7em', marginX: 'auto', display: "flex", gap: .5,
                        justifyContent: !!val ? "flex-start" : "center"
                    }} title={humanMoment}>
                        <Typography>
                            {displayDateText}
                        </Typography>
                    </Box>
                },
                customStyle: {
                    width: "15rem",
                    textAlign: "left",
                    verticalAlign: "middle",
                },
            };

            if (hasLastReceivedDate) {
                columns.splice(4, 0, lastReceivedColumn);
            }

            columns.push(
                {
                    id: "last_sold_date",
                    header: "Last Sold Date",
                    accessorKey: "last_sold_date",
                    cell: (row) => {
                        const val = row?.getValue();
                        const displayDateText = val ? moment(val, 'YYYY-MM-DD').format("MMM DD, YYYY") : "-";
                        const humanMoment = val ? moment(val, 'YYYY-MM-DD').fromNow() : "Never";
                        return <Box sx={{
                            width: '7em', marginX: 'auto', display: "flex", gap: .5,
                            justifyContent: !!val ? "flex-start" : "center"
                        }} title={humanMoment}>
                            <Typography>
                                {displayDateText}
                            </Typography>
                        </Box>
                    },
                    customStyle: {
                        width: "15rem",
                        textAlign: "left",
                        verticalAlign: "middle",
                    },
                }
            );

            return columns;
        },
        [filteredZeroSalesData, hasLastReceivedDate, filters?.store_id]
    );

    const storeZeroSalesColumns = toggleState === 'must_have' ? mustHaveColumns : innovationProductColumns;

    if (isLoadingZeroSalesData) {
        return <LoadingComponent />;
    }

    if (!filters?.store_id && !overviewTableIsEmpty) {
        return (
            <Box flexGrow={1} xs={12} sx={{ backgroundColor: "#ffffff", borderRadius: "0 0 .5rem .5rem" }} >
                <Grid container justifyContent="center" alignItems="center" py={6} borderRadius={0}>
                    <Typography
                        sx={{ fontSize: "1.5rem", color: "black", textAlign: "center" }}
                    >
                        Please select a store to view the article(s)
                    </Typography>
                </Grid>
            </Box>
        );
    }

    if (!zeroSalesData || zeroSalesData?.length === 0 || overviewTableIsEmpty || (!filters?.store_id && overviewTableIsEmpty)) {
        return (
            <Box flexGrow={1} xs={12} sx={{ backgroundColor: "#ffffff", borderRadius: "0 0 .5rem .5rem" }} >
                <Grid container justifyContent="center" alignItems="center" py={6} borderRadius={0}>
                    <Typography
                        sx={{ fontSize: "1.5rem", color: "black", textAlign: "center" }}
                    >
                        There’s no available data.
                    </Typography>
                </Grid>
            </Box>
        );
    }

    if (toggleState === 'innovation_product') {
        return (
            <Grid>
                <Grid container xs={12}>
                    <TableComponentV2
                        columns={storeZeroSalesColumns}
                        data={filteredZeroSalesData || []}
                        tableId="storeZeroSalesTable"
                        isCompact
                        customTableHeaderStyle={{ backgroundColor: "#caddeb" }}
                        customTableContainerStyle={{ borderRadius: 0, boxShadow: "none" }}
                        hasPagination
                    />
                </Grid>
            </Grid>
        )
    }

    return (
        <Grid container xs={12} sm={12} display={'flex'} justifyContent={'space-evenly'} sx={{ border: '1px solid #e0e0e0' }}>
            <Grid item xs={12} mx={1} >
                <CallOutsFilter value={actionCounts} onPillSelectChange={onPillSelectChange} />
            </Grid>
            <Grid container xs={12}>
                <TableComponentV2
                    columns={storeZeroSalesColumns}
                    data={filteredZeroSalesData || []}
                    tableId="storeZeroSalesTable"
                    isCompact
                    customTableHeaderStyle={{ backgroundColor: "#caddeb" }}
                    customTableContainerStyle={{ borderRadius: 0, boxShadow: "none" }}
                    hasPagination
                />
            </Grid>
        </Grid>
    );
}
